/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * DailyAttachPanel — the "Attach runs" entry point on a multi-day event.
 *
 * The board itself only carries a small card with one button; the work happens
 * in DailyAttachModal, which pages a day's runs from the server 100 at a time
 * (a day can hold tens of thousands). Each attached run becomes one
 * event_result_split row (see dailyAttachApi), any number per entrant per day.
 *
 * "Attached results" beside it is the way back out, as on a race: every
 * entrant with runs attached, each removable (RemoveAttachedModal, multi-day).
 *
 * Shown only to the race's owner (organizer_owns_race); for anybody else it
 * renders nothing, like the single-run panel.
 */

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { eventDayCount, eventDayNumber } from './dailyMode';
import { ownsRace } from './publishApi';
import DailyAttachModal, { dayCaption } from './DailyAttachModal';
import RemoveAttachedModal from './RemoveAttachedModal';

export { dayCaption };

const INK = '#241708';
const MUTED = '#6B5844';
const ACCENT = '#FF6A1A';
const HAIR = 'rgba(36, 23, 8, .10)';

/** Today's day of the event on the venue's calendar, kept inside the event. */
function todaysDay(eventStart, last, timezone) {
  const today = eventDayNumber(new Date().toISOString().slice(0, 19), eventStart, timezone);
  return Math.min(Math.max(today, 1), last);
}

export default function DailyAttachPanel({
  api, eventActivityId, eventStart, eventEnd, eventTimezone, minDistanceMeters = 0, signedIn = false,
}) {
  const last = eventDayCount(eventStart, eventEnd, eventTimezone);
  const [open, setOpen] = useState(false);
  const [removeOpen, setRemoveOpen] = useState(false);

  const owner = useQuery({
    queryKey: ['board', 'owns-race', eventActivityId],
    queryFn: ({ signal }) => ownsRace(api, { eventActivityId, signal }),
    enabled: Number.isFinite(eventActivityId) && signedIn,
  });
  if (owner.data !== true) return null;

  return (
    <section aria-label="Attach runs by day" style={{
      margin: '24px auto 0', maxWidth: 1160, boxSizing: 'border-box', padding: '18px 22px',
      border: `1px solid ${HAIR}`, borderRadius: 20, background: '#fff', color: INK,
      display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between',
    }}>
      <div>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>Attach runs, day by day</h2>
        <p style={{ margin: '4px 0 0', fontSize: 14, color: MUTED }}>
          This event runs for {last} days. Attach as many of each entrant&apos;s runs as they did on the day.
        </p>
      </div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <button
          type="button"
          onClick={() => setRemoveOpen(true)}
          aria-haspopup="dialog"
          style={{
            minHeight: 46, padding: '0 18px', borderRadius: 12, background: '#fff', color: INK,
            border: `1px solid ${HAIR}`, fontWeight: 700, fontSize: 15, cursor: 'pointer',
          }}
        >
          Attached results
        </button>
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-haspopup="dialog"
          style={{
            minHeight: 46, padding: '0 22px', border: 0, borderRadius: 12, background: ACCENT, color: '#fff',
            fontWeight: 700, fontSize: 15, cursor: 'pointer', boxShadow: '0 8px 20px -8px rgba(255,106,26,.7)',
          }}
        >
          Attach runs
        </button>
      </div>
      {open && (
        <DailyAttachModal
          api={api}
          eventActivityId={eventActivityId}
          eventStart={eventStart}
          eventTimezone={eventTimezone}
          lastDay={last}
          initialDay={todaysDay(eventStart, last, eventTimezone)}
          minDistanceMeters={minDistanceMeters}
          onClose={() => setOpen(false)}
        />
      )}
      {removeOpen && (
        <RemoveAttachedModal
          api={api}
          eventActivityId={eventActivityId}
          multiDay
          onClose={() => setRemoveOpen(false)}
        />
      )}
    </section>
  );
}
